// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Download,
  Eye,
  FileCheck,
  FileSignature,
  Filter,
  Mail,
  Search,
  Trash2,
  User,
  X,
  PenTool,
  Shield,
} from 'lucide-react';
import React, { useState } from 'react';
import { useSignaturesManagement, useSignatureStats } from '../hooks/useSignatures';
import { toFrench } from '@/utils/dateUtils';
import { notifications } from '@/lib/notifications';
import { Button, Spinner, Badge, SearchInput } from '@/components/ui-v2';

const SignaturesManagement: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEntityType, setFilterEntityType] = useState<string>('');
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const {
    signatures,
    total,
    isLoading,
    error,
    refetch,
    deleteSignature,
    isDeleting,
    generateProof,
    isGenerating,
  } = useSignaturesManagement({
    signedEntityType: filterEntityType || undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const { data: stats, isLoading: statsLoading } = useSignatureStats();

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none cursor-pointer text-sm`;

  // Filtrage local par recherche
  const filteredSignatures = signatures.filter((sig) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      sig.clientName.toLowerCase().includes(searchLower) ||
      sig.clientEmail.toLowerCase().includes(searchLower) ||
      sig.signedEntityId.toLowerCase().includes(searchLower)
    );
  });

  const handleGenerateProof = async (signatureId: string) => {
    try {
      await generateProof(signatureId);
      refetch();
    } catch (err: any) {
      notifications.error(err.message || t('signatures.proof.generateError'));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSignature(id);
      setDeleteConfirmId(null);
      refetch();
    } catch (err: any) {
      notifications.deleteError(t('signatures.actions.deleteErrorEntity'));
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const getEntityTypeBadge = (type: string) => {
    const config: Record<string, { variant: 'info' | 'success' | 'warning'; labelKey: string }> = {
      Quote: { variant: 'info', labelKey: 'signatures.entityTypes.Quote' },
      Invoice: { variant: 'success', labelKey: 'signatures.entityTypes.Invoice' },
      Contract: { variant: 'warning', labelKey: 'signatures.entityTypes.Contract' },
    };
    const entry = config[type];
    const variant = entry?.variant || ('info' as const);
    const label = entry ? t(entry.labelKey) : type;
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <FileSignature className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('signatures.title')}</h1>
          <p className="text-gray-500 mt-0.5">
            {t('signatures.subtitle')}
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Shield className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('signatures.legalTitle')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('signatures.legalDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('signatures.stats.total')}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-2 bg-teal-100 rounded-xl">
                <FileSignature className="w-5 h-5 text-teal-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('signatures.stats.withProof')}</p>
                <p className="text-2xl font-bold text-teal-600">{stats.withProofPdf}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-xl">
                <FileCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('signatures.stats.thisMonth')}</p>
                <p className="text-2xl font-bold text-blue-600">{stats.thisMonth}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-xl">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className={cardClasses}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">{t('signatures.stats.uniqueClients')}</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {new Set(signatures.map((s) => s.clientEmail)).size}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-xl">
                <User className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barre de filtres et recherche */}
      <div className={cardClasses}>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Recherche */}
          <div className="flex-1">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('signatures.filters.searchPlaceholder')}
            />
          </div>

          {/* Filtre par type */}
          <div className="lg:w-64">
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <select
                value={filterEntityType}
                onChange={(e) => {
                  setFilterEntityType(e.target.value);
                  setPage(0);
                }}
                className={`${selectClasses} pl-12`}
              >
                <option value="">{t('signatures.filters.allTypes')}</option>
                <option value="Quote">{t('signatures.filters.quotes')}</option>
                <option value="Invoice">{t('signatures.filters.invoices')}</option>
                <option value="Contract">{t('signatures.filters.contracts')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des signatures */}
      <div className={`${cardClasses} overflow-hidden p-0`}>
        <div className="px-6 py-4 border-b border-white/30">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-teal-600" />
            {t('signatures.list.title')}
          </h3>
        </div>

        {error ? (
          <div className="p-12 text-center">
            <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
              <X className="w-12 h-12 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('signatures.list.errorLoading')}</h3>
            <p className="text-gray-600 mb-6">{error.toString()}</p>
            <Button variant="secondary" onClick={() => refetch()}>
              {t('signatures.actions.retry')}
            </Button>
          </div>
        ) : filteredSignatures.length === 0 ? (
          <div className="p-12 text-center">
            <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
              <FileSignature className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('signatures.list.noSignatures')}</h3>
            <p className="text-gray-600">
              {searchTerm || filterEntityType
                ? t('signatures.list.modifyFilters')
                : t('signatures.list.willAppear')}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/30 border-b border-white/30">
                  <tr>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('signatures.table.client')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      {t('signatures.table.email')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('signatures.table.document')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                      {t('signatures.table.date')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden xl:table-cell">
                      {t('signatures.table.ip')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                      {t('signatures.table.proofPdf')}
                    </th>
                    <th className="px-4 md:px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('signatures.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filteredSignatures.map((signature) => (
                    <tr key={signature.id} className="hover:bg-white/30 transition-colors">
                      {/* Client */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-teal-100 rounded-xl flex items-center justify-center mr-3">
                            <span className="text-teal-600 font-semibold text-sm">
                              {signature.clientName?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {signature.clientName}
                          </span>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {signature.clientEmail}
                        </div>
                      </td>

                      {/* Document */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <div>
                          {getEntityTypeBadge(signature.signedEntityType)}
                          <p className="text-xs text-gray-500 mt-1 font-mono">
                            {signature.signedEntityId}
                          </p>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {toFrench(signature.timestamp)}
                        </div>
                      </td>

                      {/* IP */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap hidden xl:table-cell">
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
                          {signature.ipAddress}
                        </span>
                      </td>

                      {/* PDF Preuve */}
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        {signature.proofPdfUrl ? (
                          <a
                            href={signature.proofPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-medium"
                          >
                            <FileCheck className="w-4 h-4" />
                            {t('signatures.proof.available')}
                          </a>
                        ) : (
                          <button
                            onClick={() => handleGenerateProof(signature.id)}
                            disabled={isGenerating}
                            className="inline-flex items-center gap-1.5 text-sm text-yellow-600 hover:text-yellow-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Download className="w-4 h-4" />
                            {isGenerating ? t('signatures.proof.generating') : t('signatures.proof.generate')}
                          </button>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Voir l'image de signature */}
                          {signature.signatureImageUrl && (
                            <a
                              href={signature.signatureImageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-teal-600 hover:bg-teal-50 rounded-xl transition-colors"
                              title={t('signatures.actions.viewSignature')}
                            >
                              <Eye className="w-4 h-4" />
                            </a>
                          )}

                          {/* Supprimer */}
                          {deleteConfirmId === signature.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(signature.id)}
                                disabled={isDeleting}
                                className="p-2 text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors disabled:opacity-50"
                                title={t('signatures.actions.confirmDelete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                                title={t('signatures.actions.cancel')}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(signature.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                              title={t('signatures.actions.delete')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-white/30 border-t border-white/30 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {t('signatures.pagination.showing', { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, total), total, plural: total > 1 ? 's' : '' })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {t('signatures.pagination.previous')}
                  </Button>
                  <span className="px-3 py-1.5 text-sm text-gray-600 bg-white/50 rounded-xl">
                    {t('signatures.pagination.page', { current: page + 1, total: totalPages })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {t('signatures.pagination.next')}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SignaturesManagement;
